import React from 'react';
import { sortBy } from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import DataCard from 'components/common/card/DataCard';
import { convertCentsToDollars, formatMoney, formatMoneyNumericPart } from 'utils/convertMoney';

const getLatestOrderStatus = (activityLog) => sortBy(activityLog, ['id']).pop()?.status;
const getDeliveryServicePrice = ({ price }) => (price ? `$${price}` : 'Free');
const getFirstWeightLog = (weightLogs) => weightLogs.find(({ step }) => step === 1);

const getPriceString = (orderItem, isService) => {
  const { pricingType, hasMinPrice, minimumQuantity, minimumPrice, weightLogs = [] } = orderItem;

  const isPerPoundPricing = pricingType === 'PER_POUND';
  const quantityUnit = isPerPoundPricing ? 'lb' : 'unit';

  const priceProp = isService ? 'price' : 'lineItemCost';
  const perUnit = `$${orderItem[priceProp]?.toFixed(2)} / ${quantityUnit}`;

  if (hasMinPrice) {
    const minPrice = `${minimumQuantity.toFixed(2)} ${quantityUnit} @ $${minimumPrice.toFixed(2)}`;

    if (!weightLogs.length) return `${minPrice} + ${perUnit}`;

    if (isPerPoundPricing) {
      const firstWeightLog = getFirstWeightLog(weightLogs);
      const overweight = firstWeightLog?.chargeableWeight - minimumQuantity;
      const perPound =
        overweight > 0 ? ` + ${overweight.toFixed(2)} ${quantityUnit} @ ${perUnit}` : '';

      return `${minPrice}${perPound}`;
    }

    return minPrice;
  }

  return perUnit;
};

const getItemNameString = (item) =>
  item.isNameChanged ? `${item.lineItemName} (${item.baseLineItemName})` : item.lineItemName;

export const OrderSummary = ({ data, isService = false }) => {
  const {
    order,
    orderItems = [],
    lineItems = [],
    orderType,
    activityLog = [],
    orderTotal,
    placedAt,
    completedAt,
    netOrderTotal,
    roundUpAmount,
    tipAmount,
    taxAmountInCents,
    creditAmount,
    loyaltyAmount,
    promotionAmount,
    convenienceFee,
    recurringDiscountInCents,
    missedPickupFee,
  } = data;

  const {
    totalDeliveryCost: pickupTotalDeliveryCost,
    subsidyInCents: pickupSubsidyInCents,
    courierTip: pickupCourierTip,
  } = order?.allPickups?.find(({ type, status }) => type === 'PICKUP' && status === 'COMPLETED') ||
  {};
  const {
    totalDeliveryCost: deliveryTotalDeliveryCost,
    subsidyInCents: deliverySubsidyInCents,
    courierTip: deliveryCourierTip,
  } = order?.allReturnDeliveries?.find(
    ({ type, status }) => type === 'RETURN' && status === 'COMPLETED',
  ) || {};

  const isOrderStatusSubmitted =
    orderType === 'ONLINE' && getLatestOrderStatus(activityLog) === 'SUBMITTED';

  const { services, modifiers } = isService
    ? orderItems
        .filter(({ bundleId }) => !bundleId)
        .reduce(
          ({ services, modifiers }, service) => {
            const { pricingType, category, count, modifierLineItems = [], notes } = service;

            const quantityUnit = pricingType === 'PER_POUND' ? 'lbs' : '';
            const price =
              category === 'DELIVERY'
                ? getDeliveryServicePrice(service)
                : getPriceString(service, isService);
            const fixedPrice =
              category === 'FIXED_PRICE' && !isOrderStatusSubmitted
                ? ` x ${count} ${quantityUnit}`
                : '';
            const serviceName = getItemNameString(service);
            services.push(
              `${serviceName} (${price})${fixedPrice}${notes ? `. Notes: ${notes}` : ''}\n`,
            );

            modifierLineItems.map(({ quantity, modifierName, unitCost,modifierPricingType }) => {
              const modifierQuantityUnit = modifierPricingType === 'PER_POUND' ? 'lb' : 'unit';

              const quantityString = `x ${quantity} ${modifierQuantityUnit}s`;

              modifiers.push(
                `${modifierName} ($${formatMoneyNumericPart(unitCost)} / ${modifierQuantityUnit}) ${quantityString}`,
              );
            });

            return { services, modifiers };
          },
          { services: [], modifiers: [] },
        )
    : {};

  const bundles = (isService ? orderItems : lineItems)
    .filter(({ bundleId }) => !!bundleId)
    .map((bundle, i, arr) => {
      const { pricingType, category, notes } = bundle;

      const bundleCount = isService ? bundle.count : bundle.lineItemQuantity;

      const quantityUnit = pricingType === 'PER_POUND' ? 'lbs' : '';
      const price =
        category === 'DELIVERY'
          ? getDeliveryServicePrice(bundle)
          : getPriceString(bundle, isService);
      const countingString = ` x ${bundleCount} ${quantityUnit}`;
      const serviceName = getItemNameString(bundle);

      const unit = pricingType === 'PER_POUND' ? 'lb' : 'unit';

      let resultString = `${serviceName}${countingString} (${price}, original ${formatMoney(
        bundle.originalPrice,
      )} / ${unit})${notes ? `. Notes: ${notes}` : ''}\n`;

      resultString += 'Items included: ';
      resultString += bundle.items
        .map((item) => {
          const itemCount = isService ? item.count : item.lineItemQuantity;
          return `${item.lineItemName} x ${itemCount / bundleCount}`;
        })
        .join(',');

      resultString += '\n';

      const isLastElement = i === arr.length - 1;

      if (!isLastElement) {
        resultString += '\n';
      }

      return resultString;
    });

  const products = isService
    ? {}
    : lineItems
        .filter(({ bundleId }) => !bundleId)
        .map((product) => {
          const { lineItemQuantity, notes } = product;

          const productName = getItemNameString(product);

          return `${productName} (${getPriceString(product, isService)}) x ${lineItemQuantity}${
            notes ? `. Notes: ${notes}` : ''
          }\n`;
        });

  const endTime = completedAt || new Date();
  const orderDuration = moment.duration(moment(endTime).diff(moment(placedAt))).humanize();

  const details = [
    { label: 'Services', data: services, isHidden: !isService || !services.length },
    { label: 'Products', data: products, isHidden: isService || !products.length },
    { label: 'Bundles', data: bundles, isHidden: !bundles.length },
    { label: 'Modifiers', data: modifiers, isHidden: !isService },
    { label: 'Total Order Value', data: formatMoneyNumericPart(orderTotal), currency: true },

    {
      label: 'Pickup Fee',
      data: formatMoneyNumericPart(pickupTotalDeliveryCost ?? 0),
      currency: true,
    },
    {
      label: 'Pickup Subsidy',
      data: formatMoneyNumericPart(convertCentsToDollars(pickupSubsidyInCents ?? 0)),
      currency: true,
    },
    {
      label: 'On Demand Pickup Tip',
      data: formatMoneyNumericPart(pickupCourierTip ?? 0),
      currency: true,
    },
    {
      label: 'Delivery Fee',
      data: formatMoneyNumericPart(deliveryTotalDeliveryCost ?? 0),
      currency: true,
    },
    {
      label: 'Delivery Subsidy',
      data: formatMoneyNumericPart(convertCentsToDollars(deliverySubsidyInCents ?? 0)),
      currency: true,
    },
    {
      label: 'On Demand Delivery Tip',
      data: formatMoneyNumericPart(deliveryCourierTip ?? 0),
      currency: true,
    },
    { label: 'Tip Amount', data: formatMoneyNumericPart(tipAmount), currency: true },
    { label: 'Service Fee', data: formatMoneyNumericPart(convenienceFee), currency: true },
    {
      label: 'Missed Pickup Fee',
      data: formatMoneyNumericPart(missedPickupFee),
      currency: true,
    },
    ...(isService
      ? [
          {
            label: 'Tax Amount',
            data: formatMoneyNumericPart(convertCentsToDollars(taxAmountInCents ?? 0)),
            currency: true,
          },
        ]
      : []),
    { label: 'Credit Applied', data: formatMoneyNumericPart(creditAmount), currency: true },
    { label: 'Rewards Applied', data: formatMoneyNumericPart(loyaltyAmount), currency: true },
    { label: 'Promo Discount', data: formatMoneyNumericPart(promotionAmount), currency: true },
    ...(isService
      ? [
          {
            label: 'Recurring Discount',
            data: formatMoneyNumericPart(convertCentsToDollars(recurringDiscountInCents ?? 0)),
            currency: true,
          },
        ]
      : []),
    { label: 'Round Up Amount', data: roundUpAmount, currency: true },
    { label: 'Net Order Total', data: formatMoneyNumericPart(netOrderTotal), currency: true },
    { label: 'Total order duration', data: orderDuration, isHidden: !isService },
  ];

  const filteredDetails = details.filter(({ isHidden }) => !isHidden);

  return <DataCard title="Order Summary" cardData={filteredDetails} />;
};

OrderSummary.propTypes = {
  data: PropTypes.shape({
    order: PropTypes.shape({
      allPickups: PropTypes.arrayOf(
        PropTypes.shape({
          deliveryProvider: PropTypes.string,
          address1: PropTypes.string,
          city: PropTypes.string,
          firstLevelSubdivisionCode: PropTypes.string,
          totalDeliveryCost: PropTypes.number,
          courierTip: PropTypes.number,
          status: PropTypes.string,
        }),
      ),
      allReturnDeliveries: PropTypes.arrayOf(
        PropTypes.shape({
          deliveryProvider: PropTypes.string,
          address1: PropTypes.string,
          city: PropTypes.string,
          firstLevelSubdivisionCode: PropTypes.string,
          totalDeliveryCost: PropTypes.number,
          courierTip: PropTypes.number,
          status: PropTypes.string,
        }),
      ),
    }),
    orderItems: PropTypes.arrayOf(
      PropTypes.shape({
        pricingType: PropTypes.string,
        category: PropTypes.string,
        count: PropTypes.number,
        laundryType: PropTypes.string,
        price: PropTypes.number,
        hasMinPrice: PropTypes.bool,
        minimumQuantity: PropTypes.number,
        minimumPrice: PropTypes.number,
        modifierLineItems: PropTypes.arrayOf(
          PropTypes.shape({
            quantity: PropTypes.number,
            modifierName: PropTypes.string,
            unitCost: PropTypes.number,
          }),
        ),
        weightLogs: PropTypes.arrayOf(
          PropTypes.shape({ step: PropTypes.number, chargeableWeight: PropTypes.number }),
        ),
      }),
    ),
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        lineItemQuantity: PropTypes.number,
        lineItemCost: PropTypes.number,
        lineItemName: PropTypes.string,
        pricingType: PropTypes.string,
        hasMinPrice: PropTypes.bool,
        minimumQuantity: PropTypes.number,
        minimumPrice: PropTypes.number,
        weightLogs: PropTypes.arrayOf(
          PropTypes.shape({ step: PropTypes.number, chargeableWeight: PropTypes.number }),
        ),
      }),
    ),
    orderType: PropTypes.string,
    activityLog: PropTypes.arrayOf(
      PropTypes.shape({ id: PropTypes.number, status: PropTypes.string }),
    ),
    orderTotal: PropTypes.number,
    netOrderTotal: PropTypes.number,
    roundUpAmount: PropTypes.string,
    tipAmount: PropTypes.number,
    taxAmountInCents: PropTypes.number,
    creditAmount: PropTypes.number,
    loyaltyAmount: PropTypes.number,
    promotionAmount: PropTypes.number,
    convenienceFee: PropTypes.number,
    recurringDiscountInCents: PropTypes.number,
    placedAt: PropTypes.string,
    completedAt: PropTypes.string,
    missedPickupFee: PropTypes.number,
  }).isRequired,
  isService: PropTypes.bool,
};
